import { useState, useEffect } from 'react';
import { Save, Settings, AlertTriangle } from 'lucide-react';
import { policyService } from '../api/policies';
import type { ReferralPolicy } from '../types';
import toast from 'react-hot-toast';

export default function Policies() {
  const [policies, setPolicies] = useState<ReferralPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPolicies();
  }, []);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      const data = await policyService.get();
      setPolicies(data);
    } catch (error) {
      console.error('Failed to load policies:', error);
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!policies) return;

    try {
      setSaving(true);
      await policyService.update(policies);
      toast.success('Policies updated successfully');
    } catch (error) {
      console.error('Failed to save policies:', error);
      toast.error('Failed to save policies');
    } finally {
      setSaving(false);
    }
  };

  const updatePolicy = (key: keyof ReferralPolicy, value: any) => {
    if (!policies) return;
    setPolicies({ ...policies, [key]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!policies) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Failed to load policies</h3>
        <p className="mt-1 text-sm text-gray-500">Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Referral Policies</h1>
          <p className="text-gray-600">Configure referral system behavior and rules</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 disabled:opacity-50"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Save className="w-4 h-4" />
          )}
          <span>{saving ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Commission Policies */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <Settings className="w-5 h-5 text-blue-600 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Commission Policies</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">Doctor Commissions</label>
                <p className="text-sm text-gray-500">Allow internal doctors to earn commissions</p>
              </div>
              <input
                type="checkbox"
                checked={policies.allowDoctorCommissions}
                onChange={(e) => updatePolicy('allowDoctorCommissions', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">Stakeholder Payouts</label>
                <p className="text-sm text-gray-500">Enable commission payouts for stakeholders</p>
              </div>
              <input
                type="checkbox"
                checked={policies.allowStakeholderPayouts}
                onChange={(e) => updatePolicy('allowStakeholderPayouts', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">Self-Referral Blocking</label>
                <p className="text-sm text-gray-500">Block commissions for self-referrals</p>
              </div>
              <input
                type="checkbox"
                checked={policies.blockSelfReferrals}
                onChange={(e) => updatePolicy('blockSelfReferrals', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
          </div>
        </div>

        {/* Public Intake Policies */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <Settings className="w-5 h-5 text-green-600 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Public Intake Policies</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">Enable Public Referrals</label>
                <p className="text-sm text-gray-500">Allow public referral submissions via /r/:code</p>
              </div>
              <input
                type="checkbox"
                checked={policies.enablePublicReferrals}
                onChange={(e) => updatePolicy('enablePublicReferrals', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Rate Limit (per hour)
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={policies.publicReferralRateLimit}
                onChange={(e) => updatePolicy('publicReferralRateLimit', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">
                Maximum referrals per referrer per hour
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">Duplicate Detection</label>
                <p className="text-sm text-gray-500">Prevent duplicate referrals by phone + date</p>
              </div>
              <input
                type="checkbox"
                checked={policies.enableDuplicateDetection}
                onChange={(e) => updatePolicy('enableDuplicateDetection', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
          </div>
        </div>

        {/* Commission Caps */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <Settings className="w-5 h-5 text-orange-600 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Commission Caps</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Default Per-Referral Cap ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={policies.defaultPerReferralCap}
                onChange={(e) => updatePolicy('defaultPerReferralCap', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">
                Maximum commission per individual referral
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Default Monthly Cap ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={policies.defaultMonthlyCap}
                onChange={(e) => updatePolicy('defaultMonthlyCap', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">
                Maximum total commission per referrer per month
              </p>
            </div>
          </div>
        </div>

        {/* Partner Portal Policies */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <Settings className="w-5 h-5 text-purple-600 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Partner Portal</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">Enable Partner Portal</label>
                <p className="text-sm text-gray-500">Allow partners to access their portal</p>
              </div>
              <input
                type="checkbox"
                checked={policies.enablePartnerPortal}
                onChange={(e) => updatePolicy('enablePartnerPortal', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">Require OTP Authentication</label>
                <p className="text-sm text-gray-500">Require OTP for partner portal access</p>
              </div>
              <input
                type="checkbox"
                checked={policies.requirePartnerOTP}
                onChange={(e) => updatePolicy('requirePartnerOTP', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">Allow QR Downloads</label>
                <p className="text-sm text-gray-500">Let partners download their QR codes</p>
              </div>
              <input
                type="checkbox"
                checked={policies.allowPartnerQRDownload}
                onChange={(e) => updatePolicy('allowPartnerQRDownload', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Policy Summary */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Policy Summary</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Doctor commissions: {policies.allowDoctorCommissions ? 'Enabled' : 'Disabled'}</li>
                <li>Stakeholder payouts: {policies.allowStakeholderPayouts ? 'Enabled' : 'Disabled'}</li>
                <li>Public referrals: {policies.enablePublicReferrals ? 'Enabled' : 'Disabled'}</li>
                <li>Rate limit: {policies.publicReferralRateLimit} referrals/hour</li>
                <li>Per-referral cap: ${policies.defaultPerReferralCap}</li>
                <li>Monthly cap: ${policies.defaultMonthlyCap}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
